/**
 * Shared HP bar (#681) — extracted from `OwnFrame`/`TargetFrame`/
 * `FriendlyTargetFrame`/`UnitFrameStack`, which each defined a byte-identical
 * (or near-identical, modulo `height`) local `HpBar` function. Mirrors the
 * existing `FrameCastBar`/`FrameResourceBar` shared-component pattern already
 * used on these same frames.
 *
 * `testId` is the FULL data-testid string (not a prefix): unlike
 * `FrameCastBar`'s `testIdPrefix` convention, the four existing HP-bar
 * testids (`own-hp-bar`, `target-hp-bar`, `friendly-target-hp-bar`,
 * `unit-frame-hp-bar`) don't follow a uniform `{prefix}-hp-bar` shape, so
 * each call site passes its exact pre-existing testid through unchanged —
 * zero test-visible behavior change.
 *
 * Prop shape is intentionally minimal + additive-friendly: #680 (shield/
 * absorb overlay) is expected to extend this with a `shieldAmount`-style
 * prop without renaming anything here.
 */

import type { ReactElement } from "react";
import { HP_BAR_FILL_COLOR, SHIELD_BAR_FILL_COLOR } from "../theme/palette.js";
import { shieldBarGeometry } from "./hud-util.js";

export function HpBar({
  hp,
  maxHp,
  shieldAmount = 0,
  height = 10,
  testId,
}: {
  readonly hp: number;
  readonly maxHp: number;
  readonly shieldAmount?: number;
  /** Bar height, px. Defaults to the 10px convention; `UnitFrameStack` passes 8. */
  readonly height?: number;
  readonly testId: string;
}): ReactElement {
  const { healthFraction, shieldFraction, shieldWrapFraction } = shieldBarGeometry(
    hp,
    maxHp,
    shieldAmount,
  );
  return (
    <div
      data-testid={testId}
      data-hp={String(hp)}
      style={{
        width: "100%",
        height,
        background: "#111",
        border: "1px solid #333",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          width: `${healthFraction * 100}%`,
          height: "100%",
          // #344: always green — the fraction is already conveyed by width.
          background: HP_BAR_FILL_COLOR,
        }}
      />
      {shieldFraction > 0 ? (
        <div data-testid={`${testId}-shield`} style={{ position: "absolute", left: `${healthFraction * 100}%`, top: 0, width: `${shieldFraction * 100}%`, height: "100%", background: SHIELD_BAR_FILL_COLOR }} />
      ) : null}
      {shieldWrapFraction > 0 ? (
        <div data-testid={`${testId}-shield-wrap`} style={{ position: "absolute", left: 0, top: 0, width: `${shieldWrapFraction * 100}%`, height: "100%", background: SHIELD_BAR_FILL_COLOR }} />
      ) : null}
    </div>
  );
}